import { format } from "date-fns";

import { RANGE_FILTER } from "constants/Constants";
import {
  isPresenceOp,
  toWireOp,
} from "components/NewAdvancedSearch/filters/selectOperators";

// Serializes the new search's { field, value } rules into the standardized
// { op, value } envelope the backend reads. Each rule's in-memory value is
// already that envelope, so the operator travels with its filter rather than
// being folded into suffixed param names.
//
// Text, boolean and grid filters still emit their bare value: they have no
// operator UI, and their backend readers extract the value directly rather
// than through a shared parser.

const formatDate = date => format(date, "yyyy-MM-dd");

const selectionToIds = selection =>
  Array.isArray(selection) ? selection.map(o => o.id) : selection?.id;

const envelope = (op, value) =>
  JSON.stringify(value === undefined ? { op } : { op, value });

// The range and date editors carry a RANGE_FILTER op internally. Numeric
// bounds are inclusive; date bounds are strict, so After/Before mean the
// named day is excluded and On or after/On or before include it.
const NUMERIC_OP = {
  [RANGE_FILTER.GREATER_THAN]: "gte",
  [RANGE_FILTER.LESS_THAN]: "lte",
  [RANGE_FILTER.BETWEEN]: "between",
  [RANGE_FILTER.EQUALS]: "eq",
};

const DATE_OP = {
  [RANGE_FILTER.EQUALS]: "eq",
  [RANGE_FILTER.GREATER_THAN]: "gt",
  [RANGE_FILTER.LESS_THAN]: "lt",
  [RANGE_FILTER.BETWEEN]: "between",
};

const boundsPayload = ({ op, value: bounds = [] }, opsByRange, serialize) => {
  const [from, to] = bounds;
  if (from == null || from === "") return null;
  const backendOp = opsByRange[op];
  if (backendOp == null) return null;
  if (backendOp === "between") {
    if (to == null || to === "") return null;
    return envelope("between", [serialize(from), serialize(to)]);
  }
  return envelope(backendOp, serialize(from));
};

const rangePayload = value => boundsPayload(value, NUMERIC_OP, Number);
const datePayload = value => boundsPayload(value, DATE_OP, formatDate);

// A multi-value custom field carries its match mode in the operator: `in`
// matches any of the values, `all` matches every one. That retires the
// table-wide `or_filters` param, which was keyed by display label and so
// coupled every field sharing a label.
const matchModeOp = value => (value.matchAll === false ? "in" : "all");

const regularParams = (field, value) => {
  const { key, type } = field;
  switch (type) {
    case "range": {
      const payload = rangePayload(value);
      return payload ? { [key]: payload } : {};
    }
    case "date":
    case "dateRange": {
      const payload = datePayload(value);
      return payload ? { [key]: payload } : {};
    }
    // A presence operator takes no operands, so any selection left over from
    // before the operator changed stays out of the param. Every id reader
    // parses a membership envelope through the shared chokepoint, which
    // rejects one with no operands — hence the empty-list guard.
    case "select":
    case "ruvixxSelect": {
      const op = toWireOp(value.op);
      if (isPresenceOp(op)) return { [key]: envelope(op) };
      const ids = selectionToIds(value.value);
      if (ids == null) return {};
      const list = Array.isArray(ids) ? ids : [ids];
      if (!list.length) return {};
      return { [key]: envelope(op, list) };
    }
    default:
      return { [key]: value.value };
  }
};

const customFieldParams = (field, value) => {
  switch (field.type) {
    case "select": {
      const ids = selectionToIds(value.value);
      if (ids == null) return {};
      const list = Array.isArray(ids) ? ids : [ids];
      if (!list.length) return {};
      // A single select has one value, so match-any and match-all agree.
      const op = field.options?.type === "multi" ? matchModeOp(value) : "in";
      return { [field.key]: envelope(op, list) };
    }
    case "list": {
      const values = value.value ?? [];
      return values.length
        ? { [field.key]: envelope(matchModeOp(value), values) }
        : {};
    }
    case "numeric": {
      const payload = rangePayload(value);
      return payload ? { [field.key]: payload } : {};
    }
    case "date": {
      const payload = datePayload(value);
      return payload ? { [field.key]: payload } : {};
    }
    default:
      return { [field.key]: value.value };
  }
};

const tagClause = ({ op, value: ids = [] }) =>
  ids.length ? { op: op === "excludes" ? "not_in" : "in", value: ids } : null;

export const buildQueryParams = rules => {
  const params = {};
  const tagClauses = [];

  rules.forEach(({ field, value }) => {
    if (!field || value == null) return;
    // Every tag group rides one param as its own clause, so no per-group key
    // suffix is needed to keep them apart.
    if (field.type === "tag") {
      const clause = tagClause(value);
      if (clause) tagClauses.push(clause);
      return;
    }
    // A grid is a set of independent boolean flags; each selected option's key
    // becomes its own `key=true` param.
    if (field.type === "grid") {
      (value.value ?? []).forEach(option => {
        params[option.id] = true;
      });
      return;
    }
    if (field.type === "listFilter") {
      const values = value.value ?? [];
      if (values.length) {
        params[field.key] = envelope(
          value.op === "excludes" ? "not_in" : "in",
          values
        );
      }
      return;
    }
    Object.assign(
      params,
      field.isCustomField
        ? customFieldParams(field, value)
        : regularParams(field, value)
    );
  });

  if (tagClauses.length) {
    params.tags = JSON.stringify(tagClauses);
  }

  return params;
};
